import { ChevronNextIcon, ChevronPrevIcon, QuotationMarkIcon } from '@svgs'
import React from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { EffectFade, Navigation, Autoplay } from 'swiper/modules'
import { useTranslation } from 'react-i18next'
import { useGetPublicStatsQuery } from 'src/redux/api'
import { AVATAR_COLORS } from 'src/components/elements/initials-avatar/InitialsAvatar'

import 'swiper/css'

import image1 from '@images/testimonials/image_1.jpg'

const TESTIMONIAL_COUNT = 6

const TestimonialSection: React.FC = () => {
  const { t } = useTranslation()
  const { data: stats } = useGetPublicStatsQuery()

  const totalUsers = stats?.totalUsers ?? 0
  const formattedCount = totalUsers.toLocaleString('uk-UA')

  return (
    // people's stories beside the sign-in forms; no text under 12px (a
    // phone had the city at 10px and the story at 12px)
    <aside
      data-testimonials
      className="md:rounded-2xl overflow-hidden flex flex-col w-full md:max-w-[58.5rem] relative min-h-[17.5rem] md:min-h-[40rem]"
    >
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
        <img loading="lazy" src={image1} alt="" className="object-cover size-full" />
        <div className="w-full absolute bottom-0 left-0 h-[23.1875rem] opacity-90 bg-gradient-to-b from-transparent to-black pointer-events-none" />
      </div>
      <div className="flex flex-col gap-6 px-4 py-8 md:p-8 md:pt-12 relative h-full z-10">
        {/* Only a real count — "3 289" (or "...") was shown whenever the
            stats were empty or still loading. */}
        {totalUsers > 0 && (
          <div className="inline-flex gap-2.5 flex-wrap text-sm md:text-base items-center self-start px-4 md:px-6 py-2 md:py-3 bg-surface rounded-full text-ink">
            <strong className="text-base md:text-2xl font-extrabold">{formattedCount}</strong>
            <span>{t('clients_found_dream_home')}</span>
          </div>
        )}
        <div className="mt-auto flex flex-col md:flex-row gap-10 w-full relative">
          <Swiper
            loop
            modules={[Autoplay, EffectFade, Navigation]}
            effect="fade"
            fadeEffect={{ crossFade: true }}
            virtualTranslate={true}
            autoplay={{
              delay: 4000,
              disableOnInteraction: true,
            }}
            speed={800}
            navigation={{ nextEl: '.testimonial-arrow-next', prevEl: '.testimonial-arrow-prev' }}
            className="flex w-full h-auto"
          >
            {Array.from({ length: TESTIMONIAL_COUNT }, (_, i) => {
              const name = t(`testimonials.${i}.name`)
              return (
                <SwiperSlide key={i} className="flex flex-col justify-end !h-auto">
                  <blockquote className="flex flex-col gap-4 m-0 md:max-w-[44.875rem] w-full text-sm leading-5 md:text-base md:leading-6 text-white">
                    <div className="icon-button fill-white w-4 h-4 md:h-8 md:w-8">
                      <QuotationMarkIcon />
                    </div>
                    <p className="m-0 self-stretch">{t(`testimonials.${i}.text`)}</p>
                    <div className="flex gap-2.5 items-center pr-[7rem] md:pr-[9rem]">
                      <div
                        aria-hidden="true"
                        className={`w-8 h-8 md:w-[3.75rem] md:h-[3.75rem] flex-shrink-0 rounded-full overflow-hidden flex items-center justify-center text-white font-bold text-sm md:text-2xl ${AVATAR_COLORS[i % AVATAR_COLORS.length]}`}
                      >
                        {name.charAt(0)}
                      </div>
                      <div className="">
                        <strong className="text-sm md:text-lg font-bold leading-5 md:leading-7">
                          {name}
                        </strong>
                        <br />
                        <span className="italic text-xs leading-4 md:text-base md:leading-6 text-white/85">
                          {t(`testimonials.${i}.city`)}
                        </span>
                      </div>
                    </div>
                  </blockquote>
                </SwiperSlide>
              )
            })}
          </Swiper>

          <div className="flex gap-4 items-center shrink-0 absolute bottom-0 right-0 z-10">
            <button
              aria-label={t('previous_testimonial')}
              className="testimonial-arrow-prev shrink-0 flex items-center justify-center w-11 h-11 md:w-12 md:h-12 border border-solid border-white/50 rounded-full fill-white hover:border-white"
            >
              <div className="icon-button w-3 h-3 md:w-4 md:h-4">
                <ChevronPrevIcon />
              </div>
            </button>
            <button
              aria-label={t('next_testimonial')}
              className="testimonial-arrow-next shrink-0 flex items-center justify-center w-11 h-11 md:w-12 md:h-12 border border-solid border-white/50 rounded-full fill-white hover:border-white"
            >
              <div className="icon-button w-3 h-3 md:w-4 md:h-4">
                <ChevronNextIcon />
              </div>
            </button>
          </div>
        </div>
      </div>
    </aside>
  )
}

export default TestimonialSection
